import React, { useState, useEffect } from "react";
import { useForm, SubmitHandler } from "react-hook-form";
import { useDispatch } from "react-redux";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { setToken } from "../redux/slices/authSlice";
import { useResetPasswordMutation } from "../api/api";
import { useToast } from "../@/components/ui/use-toast";
import { AiOutlineEye, AiOutlineEyeInvisible } from "react-icons/ai";
import { validateField } from "../lib/utils";
import { MIN_PASSWORD_LENGTH, REGEXES } from "../lib/constants";
import { MdLockOutline } from "react-icons/md";
import { IoAlertCircleOutline } from "react-icons/io5";

interface FormData {
  newPassword: string;
  confirmPassword: string;
  oldPassword?: string | null;
}

const ResetPassword = () => {
  const {
    setError,
    register,
    handleSubmit,
    getValues,
    formState: { errors },
  } = useForm<FormData>();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const isAdmin = queryParams.get("isAdmin");
  const [searchParams] = useSearchParams();
  const isShowOldPasswordInput =
    searchParams.get("isShowOldPasswordInput") !== "false";
  const [resetPasswordApi, { isLoading: isResettingPassword }] =
    useResetPasswordMutation();
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [showOldPassword, setShowOldPassword] = useState(false);
  const [showOldPasswordInput, setShowOldPasswordInput] = useState(
    isShowOldPasswordInput
  );

  const { toast } = useToast();

  useEffect(() => {
    setShowOldPasswordInput(isShowOldPasswordInput);
  }, [isShowOldPasswordInput]);

  const onSubmitPassword: SubmitHandler<FormData> = async (data) => {
    const passwordError = validateField("password", data.newPassword, {
      required: true,
      minLength: 6,
      context: "signup",
    });
    if (passwordError) {
      setError("newPassword", {
        type: "manual",
        message: passwordError,
      });
      return;
    }

    if (data.newPassword !== data.confirmPassword) {
      setError("confirmPassword", {
        type: "manual",
        message: "Passwords do not match",
      });
      return;
    }

    try {
      const payload: any = {
        newPassword: data.newPassword,
        role: isAdmin ? "ADMIN" : "VENDOR",
      };

      if (showOldPasswordInput && data.oldPassword) {
        payload.oldPassword = data.oldPassword;
      }

      const response = await resetPasswordApi({ body: payload }).unwrap();
      if (response.token) {
        toast({
          title: "Success",
          description: "Password Updated Successfully",
        });
        dispatch(setToken(response.token));
        navigate("/dashboard");
      }
    } catch (error: any) {
      toast({
        title: "Error",
        description: `${error.data.message.split("ErrorCode")[0].trim()}`,
      });
    }
  };

  return (
    <div className="flex justify-center mt-8 px-6">
      <div className="bg-white w-full md:w-1/2 lg:w-1/3 p-8 rounded-xl shadow-lg border border-gray-100">
        {/* Header Section with Icon */}
        <div className="text-center mb-8">
          <div className="bg-customGreenColor/10 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
            <MdLockOutline className="h-8 w-8 text-customGreenColor" />
          </div>
          <h2 className="text-2xl font-bold text-gray-800 mb-2">
            Reset Password
          </h2>
          <p className="text-sm text-gray-600">
            Create a new secure password for your account
          </p>
        </div>

        <form onSubmit={handleSubmit(onSubmitPassword)} className="space-y-6">
          {showOldPasswordInput && (
            <div className="space-y-1">
              <label
                htmlFor="oldPassword"
                className="block text-sm font-medium text-gray-700"
              >
                Old Password<span className="text-red-500 ml-1">*</span>
              </label>
              <div className="relative">
                <input
                  {...register("oldPassword", {
                    required: showOldPasswordInput
                      ? "Old Password is required"
                      : false,
                  })}
                  className={`w-full px-4 py-3 rounded-lg border ${
                    errors.oldPassword ? "border-red-500" : "border-gray-300"
                  } focus:border-customGreenColor focus:ring-2 focus:ring-customGreenColor/20 focus:outline-none transition duration-200`}
                  placeholder="Enter your old password"
                  id="oldPassword"
                  type={showOldPassword ? "text" : "password"}
                />
                <button
                  type="button"
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                  onClick={() => setShowOldPassword(!showOldPassword)}
                >
                  {showOldPassword ? (
                    <AiOutlineEyeInvisible className="text-xl" />
                  ) : (
                    <AiOutlineEye className="text-xl" />
                  )}
                </button>
              </div>
              {errors.oldPassword && (
                <p className="text-sm text-red-500 mt-1 flex items-center">
                  <IoAlertCircleOutline className="h-4 w-4 mr-1" />
                  {errors.oldPassword?.message}
                </p>
              )}
            </div>
          )}

          <div className="space-y-1">
            <label
              htmlFor="newPassword"
              className="block text-sm font-medium text-gray-700"
            >
              New Password<span className="text-red-500 ml-1">*</span>
            </label>
            <div className="relative">
              <input
                {...register("newPassword")}
                className={`w-full px-4 py-3 rounded-lg border ${
                  errors.newPassword ? "border-red-500" : "border-gray-300"
                } focus:border-customGreenColor focus:ring-2 focus:ring-customGreenColor/20 focus:outline-none transition duration-200`}
                placeholder="Enter your new password"
                id="newPassword"
                type={showPassword ? "text" : "password"}
              />
              <button
                type="button"
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                onClick={() => setShowPassword(!showPassword)}
              >
                {showPassword ? (
                  <AiOutlineEyeInvisible className="text-xl" />
                ) : (
                  <AiOutlineEye className="text-xl" />
                )}
              </button>
            </div>
            {errors.newPassword && (
              <p className="text-sm text-red-500 mt-1 flex items-center">
                <IoAlertCircleOutline className="h-4 w-4 mr-1" />
                {errors.newPassword.message}
              </p>
            )}
          </div>

          <div className="space-y-1">
            <label
              htmlFor="confirmPassword"
              className="block text-sm font-medium text-gray-700"
            >
              Confirm Password<span className="text-red-500 ml-1">*</span>
            </label>
            <div className="relative">
              <input
                {...register("confirmPassword")}
                className={`w-full px-4 py-3 rounded-lg border ${
                  errors.confirmPassword ? "border-red-500" : "border-gray-300"
                } focus:border-customGreenColor focus:ring-2 focus:ring-customGreenColor/20 focus:outline-none transition duration-200`}
                placeholder="Confirm your new password"
                id="confirmPassword"
                type={showConfirmPassword ? "text" : "password"}
              />
              <button
                type="button"
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              >
                {showConfirmPassword ? (
                  <AiOutlineEyeInvisible className="text-xl" />
                ) : (
                  <AiOutlineEye className="text-xl" />
                )}
              </button>
            </div>
            {errors.confirmPassword && (
              <p className="text-sm text-red-500 mt-1 flex items-center">
                <IoAlertCircleOutline className="h-4 w-4 mr-1" />
                {errors.confirmPassword.message}
              </p>
            )}
          </div>

          <button
            disabled={isResettingPassword}
            type="submit"
            className="w-full py-3 px-4 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-customGreenColor hover:bg-customGreenColor/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-customGreenColor disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 ease-in-out mt-6"
          >
            {isResettingPassword ? (
              <div className="flex items-center justify-center">
                <div className="loader mr-2" />
                <span>Resetting Password...</span>
              </div>
            ) : (
              "Reset Password"
            )}
          </button>
        </form>
      </div>
    </div>
  );
};

export default ResetPassword;
